// Phase 1A Phase 9C.4 — useHomeStats hook + pure derivation.
//
// Composes existing read-only hooks (useRolloverChain,
// useRolloverProtection, useChainProtectedSeries, useSignalWatchlist)
// into a 4-stat scan-strip view-model for HomeStatStrip.
//
// Trust contract:
//   - No writes. No RPC. No backend changes. No localStorage. No URL state.
//   - Only stat with a trend delta is Protected — derived from the
//     last two points of the existing useChainProtectedSeries series.
//     Other stats have no historical snapshot to derive deltas from
//     and never imply trend.
//   - Pool B can decrease in real Phase-7B math after a busted cycle;
//     a negative trend is truthful, not misleading.
//
// The pure `deriveHomeStats` helper is exported for tests so coverage
// stays DOM-free and doesn't require jsdom.

import { useMemo } from "react";
import type { IconKey } from "../../components/icons/icon-types.ts";
import { hasIcon } from "../../components/icons/icons.ts";
import { useSignalWatchlist } from "../../exchange/useSignalWatchlist.ts";
import { useRolloverChain } from "../../vault/useRolloverChain.ts";
import { useRolloverProtection } from "../../vault/useRolloverProtection.ts";
import { useChainProtectedSeries } from "./useChainProtectedSeries.ts";

const NO_CYCLE_IDS: readonly string[] = [];

export type HomeStatKey = "cycles" | "protected" | "decisions" | "watched";

export interface HomeStat {
  key: HomeStatKey;
  eyebrow: string;
  value: string;
  caption: string;
  iconKey: IconKey;
  trendText?: string;
  trendTone?: "success" | "danger" | "neutral";
}

export interface HomeStatsInput {
  chain: {
    current_week: number | null;
    total_rollovers: number | null;
  } | null;
  cumulativeProtected: number | null;
  protectedSeries: ReadonlyArray<{ cumulativeProtected: number }>;
  decisionCount: number;
  watchedCount: number;
}

// ─── Pure helpers ────────────────────────────────────────────────

function fmtMoney(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return `GH₵${v.toLocaleString()}`;
}

function fmtMoneyDelta(v: number): string {
  if (!Number.isFinite(v)) return "0";
  return `GH₵${Math.abs(v).toLocaleString()}`;
}

function fmtCount(v: number): string {
  return Number.isFinite(v) ? String(Math.max(0, Math.trunc(v))) : "0";
}

function deriveCyclesStat(chain: HomeStatsInput["chain"]): HomeStat {
  const cw =
    chain && typeof chain.current_week === "number" && Number.isFinite(chain.current_week)
      ? chain.current_week
      : 0;
  const tot =
    chain && typeof chain.total_rollovers === "number" && Number.isFinite(chain.total_rollovers)
      ? chain.total_rollovers
      : 0;
  const value = `${String(cw)} / ${String(tot)}`;
  let caption: string;
  if (chain === null || tot <= 0) {
    caption = "No chain yet";
  } else {
    const pct = Math.round((cw / tot) * 100);
    caption = `Week ${String(cw)} of ${String(tot)} · ${String(pct)}%`;
  }
  return {
    key: "cycles",
    eyebrow: "CYCLES",
    value,
    caption,
    iconKey: "domain.rollover",
  };
}

function deriveProtectedStat(
  cumulativeProtected: number | null,
  series: ReadonlyArray<{ cumulativeProtected: number }>,
): HomeStat {
  const value = fmtMoney(cumulativeProtected);
  const stat: HomeStat = {
    key: "protected",
    eyebrow: "PROTECTED",
    value,
    caption: "Pool B · Bankroll Pool",
    iconKey: "reserve.bankroll_pool",
  };
  if (series.length < 2) {
    stat.trendText = "No prior cycle yet";
    stat.trendTone = "neutral";
    return stat;
  }
  const last = series[series.length - 1]!.cumulativeProtected;
  const prev = series[series.length - 2]!.cumulativeProtected;
  const delta = last - prev;
  if (delta > 0) {
    stat.trendText = `▲ +${fmtMoneyDelta(delta)}`;
    stat.trendTone = "success";
  } else if (delta < 0) {
    stat.trendText = `▼ −${fmtMoneyDelta(delta)}`;
    stat.trendTone = "danger";
  } else {
    stat.trendText = "No change";
    stat.trendTone = "neutral";
  }
  return stat;
}

function deriveDecisionsStat(decisionCount: number): HomeStat {
  return {
    key: "decisions",
    eyebrow: "DECISIONS",
    value: fmtCount(decisionCount),
    caption: "Compounding decisions logged",
    iconKey: "decision.confirm",
  };
}

function deriveWatchedStat(watchedCount: number): HomeStat {
  return {
    key: "watched",
    eyebrow: "WATCHED",
    value: fmtCount(watchedCount),
    caption: "Saved observation signals",
    iconKey: "action.filter",
  };
}

export function deriveHomeStats(input: HomeStatsInput): HomeStat[] {
  const stats: HomeStat[] = [
    deriveCyclesStat(input.chain),
    deriveProtectedStat(input.cumulativeProtected, input.protectedSeries),
    deriveDecisionsStat(input.decisionCount),
    deriveWatchedStat(input.watchedCount),
  ];
  // Defensive: every emitted iconKey must resolve to a real icon. If
  // a future change emits an unknown key, fail loudly during dev.
  for (const s of stats) {
    if (!hasIcon(s.iconKey)) {
      throw new Error(`useHomeStats: unknown iconKey "${String(s.iconKey)}" for ${s.key}`);
    }
  }
  return stats;
}

// ─── React hook ──────────────────────────────────────────────────

export function useHomeStats(): HomeStat[] {
  const { chain } = useRolloverChain();
  const cycleIds = useMemo(() => [...NO_CYCLE_IDS], []);
  const protection = useRolloverProtection(chain?.id ?? null, cycleIds);
  const { series } = useChainProtectedSeries(chain?.id ?? null);
  const watchlist = useSignalWatchlist();
  return useMemo(
    () =>
      deriveHomeStats({
        chain: chain
          ? { current_week: chain.current_week, total_rollovers: chain.total_rollovers }
          : null,
        cumulativeProtected: protection.cumulativeProtected,
        protectedSeries: series,
        decisionCount: protection.decisionCount,
        watchedCount: watchlist.watchedFixtureIds.size,
      }),
    [
      chain,
      protection.cumulativeProtected,
      protection.decisionCount,
      series,
      watchlist.watchedFixtureIds,
    ],
  );
}
